import React, { useState, useEffect, useContext } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Bell,
  Send,
  Users,
  Megaphone,
  Mail,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Calendar,
  Clock,
  AlertCircle,
  X,
  ExternalLink,
} from "lucide-react";
import {
  Notice,
  CreateNoticeRequest,
  getAllNotices,
  createNotice,
  deleteNotice,
  updateNotice,
  markNoticeAsRead,
  hideNotice,
} from "../../services/notices";
import { AuthContext } from "../../contexts/AuthContext";
import { toast } from "react-hot-toast";
import Pagination from "../../components/common/Pagination";

const SecretaryAnnouncements: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  const [formData, setFormData] = useState<CreateNoticeRequest>({
    title: "",
    content: "",
    type: "general",
    priority: "medium",
    targetAudience: ["all"],
    publishDate: new Date().toISOString().split("T")[0],
    expiryDate: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const queryClient = useQueryClient();

  // Check if current user can edit/delete a notice
  const canEditNotice = (notice: Notice): boolean => {
    if (!currentUser || !notice || !notice.author || !notice.author.userId)
      return false;

    // Handle both populated and non-populated author.userId
    const authorUserId =
      typeof notice.author.userId === "string"
        ? notice.author.userId
        : notice.author.userId &&
            typeof notice.author.userId === "object" &&
            notice.author.userId._id
          ? notice.author.userId._id
          : null;

    if (!authorUserId) return false;

    const currentUserId = String(currentUser.id);

    return String(authorUserId) === currentUserId;
  };

  // Get all notices (secretaries can see all branch notices)
  const { data: noticesResponse, isLoading } = useQuery({
    queryKey: ["notices", { page: currentPage, limit: itemsPerPage }],
    queryFn: () => getAllNotices({ page: currentPage, limit: itemsPerPage }),
  });

  const notices = noticesResponse?.data || [];

  // Filter out notices with invalid data
  const validNotices = notices.filter(
    (notice: any) =>
      notice &&
      typeof notice === "object" &&
      notice._id &&
      notice.author &&
      typeof notice.author === "object" &&
      notice.author.userId,
  );

  // Create notice mutation
  const createNoticeMutation = useMutation({
    mutationFn: createNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      setShowCreateModal(false);
      resetForm();
      toast.success("Announcement created successfully!");
    },
    onError: () => {
      toast.error("Failed to create announcement");
    },
  });

  // Update notice mutation
  const updateNoticeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateNoticeRequest }) =>
      updateNotice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      setShowEditModal(false);
      setSelectedNotice(null);
      resetForm();
      toast.success("Announcement updated successfully!");
    },
    onError: () => {
      toast.error("Failed to update announcement");
    },
  });

  // Delete notice mutation
  const deleteNoticeMutation = useMutation({
    mutationFn: deleteNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      toast.success("Announcement deleted successfully!");
    },
    onError: () => {
      toast.error("Failed to delete announcement");
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: markNoticeAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
    },
    onError: () => {
      toast.error("Failed to mark announcement as read");
    },
  });

  // Hide notice mutation
  const hideNoticeMutation = useMutation({
    mutationFn: hideNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      queryClient.refetchQueries({ queryKey: ["notices"] });
      toast.success("Announcement hidden successfully");
    },
    onError: () => {
      toast.error("Failed to hide announcement");
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      type: "general",
      priority: "medium",
      targetAudience: ["all"],
      publishDate: new Date().toISOString().split("T")[0],
      expiryDate: "",
    });
  };

  const handleCreate = () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    createNoticeMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!selectedNotice || !formData.title.trim() || !formData.content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    updateNoticeMutation.mutate({ id: selectedNotice._id, data: formData });
  };

  const handleViewNotice = (notice: Notice) => {
    setSelectedNotice(notice);
    setShowViewModal(true);
  };

  const handleEdit = (notice: Notice) => {
    setSelectedNotice(notice);
    setFormData({
      title: notice.title,
      content: notice.content,
      type: notice.type,
      priority: notice.priority,
      targetAudience: notice.targetAudience,
      publishDate: notice.publishDate?.split("T")[0] || "",
      expiryDate: notice.expiryDate?.split("T")[0] || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = (noticeId: string) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      deleteNoticeMutation.mutate(noticeId);
    }
  };

  const handleMarkAsRead = (noticeId: string) => {
    markAsReadMutation.mutate(noticeId);
  };

  const handleHideNotice = (noticeId: string) => {
    hideNoticeMutation.mutate(noticeId);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-gradient-to-br from-red-300 to-pink-400 text-white font-bold shadow-sm";
      case "medium":
        return "bg-gradient-to-br from-amber-300 to-yellow-400 text-white font-bold shadow-sm";
      case "low":
        return "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white font-bold shadow-sm";
      default:
        return "bg-gradient-to-br from-slate-300 to-slate-400 text-white font-bold shadow-sm";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "urgent":
        return "bg-gradient-to-br from-red-300 to-pink-400 text-white font-bold shadow-sm";
      case "important":
        return "bg-gradient-to-br from-amber-300 to-yellow-400 text-white font-bold shadow-sm";
      case "academic":
        return "bg-gradient-to-br from-blue-300 to-indigo-400 text-white font-bold shadow-sm";
      case "info":
        return "bg-gradient-to-br from-purple-300 to-purple-400 text-white font-bold shadow-sm";
      default:
        return "bg-gradient-to-br from-slate-300 to-slate-400 text-white font-bold shadow-sm";
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-slate-200/60">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Announcements & Communications
              </h1>
              <p className="text-slate-600 mt-1 font-medium">
                Send announcements to students and teachers in your branch
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-indigo-300 to-indigo-400 hover:shadow-xl hover:scale-105 text-white px-6 py-3 rounded-xl flex items-center gap-2 transition-all duration-300 font-bold shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Create Announcement
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-600">
                  Active Notices (This Page)
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {validNotices.filter((n) => n.isActive).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center shadow-md">
                <Bell className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-600">
                  High Priority (This Page)
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {validNotices.filter((n) => n.priority === "high").length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center shadow-md">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-600">
                  Total Notices
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {noticesResponse?.pagination?.totalItems ||
                    validNotices.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md">
                <Megaphone className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Notices List */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60">
          <div className="p-6 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
            <h2 className="text-xl font-bold text-slate-900">
              All Announcements
            </h2>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-indigo-500"></div>
              <p className="mt-4 text-slate-600 font-medium">
                Loading announcements...
              </p>
            </div>
          ) : notices.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center mx-auto mb-4 shadow-md">
                <Bell className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                No announcements yet
              </h3>
              <p className="text-slate-600 mb-4 font-medium">
                Create your first announcement to get started
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-indigo-300 to-indigo-400 text-white px-6 py-2.5 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold"
              >
                Create Announcement
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-200/60">
              {validNotices.map((notice) => (
                <div
                  key={notice._id}
                  className={`p-6 hover:bg-slate-50/50 transition-all duration-300 border-l-4 ${
                    !notice.isRead
                      ? "border-l-blue-400 bg-blue-50/30"
                      : "border-l-slate-200"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3
                          className={`text-lg font-bold ${
                            !notice.isRead ? "text-slate-900" : "text-slate-700"
                          }`}
                        >
                          {notice.title}
                        </h3>
                        {!notice.isRead && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full shadow-sm"></div>
                        )}
                        <span
                          className={`px-3 py-1 text-xs rounded-xl ${getTypeColor(
                            notice.type,
                          )}`}
                        >
                          {notice.type}
                        </span>
                        <span
                          className={`px-3 py-1 text-xs rounded-xl ${getPriorityColor(
                            notice.priority,
                          )}`}
                        >
                          {notice.priority}
                        </span>
                        {!notice.isActive && (
                          <span className="px-3 py-1 text-xs font-bold rounded-xl bg-gradient-to-br from-slate-300 to-slate-400 text-white shadow-sm">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div
                        onClick={() => handleViewNotice(notice)}
                        className="cursor-pointer hover:bg-slate-100/50 -mx-2 px-2 py-2 rounded-lg transition-colors"
                      >
                        <p className="text-slate-600 mb-1 line-clamp-2 font-medium">
                          {notice.content}
                        </p>
                        <span className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          Click to view full content
                        </span>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-slate-500 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                            <Users className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold capitalize">
                            {Array.isArray(notice.targetAudience)
                              ? notice.targetAudience.join(", ")
                              : notice.targetAudience}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold">
                            {new Date(notice.publishDate).toLocaleDateString()}
                          </span>
                        </div>
                        {notice.expiryDate && (
                          <div className="flex items-center gap-1.5">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center">
                              <Clock className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-semibold">
                              Expires:{" "}
                              {new Date(notice.expiryDate).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {!notice.isRead && !canEditNotice(notice) && (
                        <button
                          onClick={() => handleMarkAsRead(notice._id)}
                          disabled={markAsReadMutation.isPending}
                          className="px-4 py-2 bg-gradient-to-r from-indigo-300 to-indigo-400 text-white rounded-xl text-sm font-bold hover:shadow-md hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {markAsReadMutation.isPending
                            ? "Marking..."
                            : "Mark as Read"}
                        </button>
                      )}
                      <button
                        onClick={() => handleHideNotice(notice._id)}
                        disabled={hideNoticeMutation.isPending}
                        className="px-4 py-2 bg-gradient-to-r from-gray-300 to-gray-400 text-white rounded-xl text-sm font-bold hover:shadow-md hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {hideNoticeMutation.isPending ? "Hiding..." : "Hide"}
                      </button>
                      {canEditNotice(notice) && (
                        <>
                          <button
                            onClick={() => handleEdit(notice)}
                            className="p-2.5 rounded-xl bg-gradient-to-br from-blue-300 to-indigo-400 text-white hover:shadow-md hover:scale-110 transition-all duration-300"
                            title="Edit announcement"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(notice._id)}
                            className="p-2.5 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 text-white hover:shadow-md hover:scale-110 transition-all duration-300"
                            title="Delete announcement"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {!canEditNotice(notice) && notice.isRead && (
                        <div className="text-xs text-slate-500 px-3 py-1.5 bg-slate-100 rounded-lg font-semibold">
                          Created by {notice.author?.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {validNotices.length > 0 && noticesResponse?.pagination && (
            <div className="px-6 py-4 border-t border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
              <Pagination
                currentPage={currentPage}
                totalPages={noticesResponse.pagination.totalPages}
                onPageChange={setCurrentPage}
                pageSize={itemsPerPage}
                onPageSizeChange={(size) => {
                  setItemsPerPage(size);
                  setCurrentPage(1);
                }}
                showPageSizeSelector={true}
                className="w-full"
              />
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200/60 shadow-2xl">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200/60">
              <h3 className="text-xl font-bold text-slate-900">
                Create Announcement
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl p-2 transition-all duration-300 hover:scale-110"
              >
                <Plus className="w-6 h-6 rotate-45" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all placeholder:text-slate-400"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  rows={6}
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all placeholder:text-slate-400"
                  placeholder="Enter announcement content"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all"
                  >
                    <option value="general">General</option>
                    <option value="academic">Academic</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                    <option value="info">Information</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as any,
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">
                  Target Audience
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { value: "all", label: "All Users" },
                    { value: "students", label: "Students" },
                    { value: "teachers", label: "Teachers" },
                    { value: "staff", label: "Staff" },
                    { value: "parents", label: "Parents" },
                  ].map((audience) => (
                    <label
                      key={audience.value}
                      className="flex items-center gap-2 cursor-pointer hover:bg-indigo-50 p-2 rounded-lg transition-colors border border-slate-200"
                    >
                      <input
                        type="checkbox"
                        checked={
                          formData.targetAudience?.includes(
                            audience.value as any,
                          ) || false
                        }
                        onChange={(e) => {
                          const currentAudiences =
                            formData.targetAudience || [];
                          let newAudiences: typeof formData.targetAudience;

                          if (e.target.checked) {
                            // If selecting "all", clear other selections
                            if (audience.value === "all") {
                              newAudiences = ["all"];
                            } else {
                              // Remove "all" if present and add new selection
                              newAudiences = [
                                ...currentAudiences.filter((a) => a !== "all"),
                                audience.value as any,
                              ];
                            }
                          } else {
                            // Remove the unchecked audience
                            newAudiences = currentAudiences.filter(
                              (a) => a !== audience.value,
                            );
                            // If no audiences selected, default to "all"
                            if (newAudiences.length === 0) {
                              newAudiences = ["all"];
                            }
                          }

                          setFormData({
                            ...formData,
                            targetAudience: newAudiences,
                          });
                        }}
                        className="w-4 h-4 text-indigo-400 border-slate-300 rounded focus:ring-indigo-400 focus:ring-2"
                      />
                      <span className="text-slate-700 font-semibold text-sm">
                        {audience.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={formData.publishDate}
                    onChange={(e) =>
                      setFormData({ ...formData, publishDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expiryDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200/60">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-6 py-2.5 text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-all duration-300 hover:scale-105 font-bold shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createNoticeMutation.isPending}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-300 to-indigo-400 text-white rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed font-bold flex items-center gap-2"
              >
                {createNoticeMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  "Create Announcement"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedNotice && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200/60 shadow-2xl">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200/60">
              <h3 className="text-xl font-bold text-slate-900">
                Edit Announcement
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedNotice(null);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl p-2 transition-all duration-300 hover:scale-110"
              >
                <Plus className="w-6 h-6 rotate-45" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all placeholder:text-slate-400"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  rows={6}
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all placeholder:text-slate-400"
                  placeholder="Enter announcement content"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all"
                  >
                    <option value="general">General</option>
                    <option value="academic">Academic</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                    <option value="info">Information</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as any,
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3">
                  Target Audience
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { value: "all", label: "All Users" },
                    { value: "students", label: "Students" },
                    { value: "teachers", label: "Teachers" },
                    { value: "staff", label: "Staff" },
                    { value: "parents", label: "Parents" },
                  ].map((audience) => (
                    <label
                      key={audience.value}
                      className="flex items-center gap-2 cursor-pointer hover:bg-indigo-50 p-2 rounded-lg transition-colors border border-slate-200"
                    >
                      <input
                        type="checkbox"
                        checked={
                          formData.targetAudience?.includes(
                            audience.value as any,
                          ) || false
                        }
                        onChange={(e) => {
                          const currentAudiences =
                            formData.targetAudience || [];
                          let newAudiences: typeof formData.targetAudience;

                          if (e.target.checked) {
                            // If selecting "all", clear other selections
                            if (audience.value === "all") {
                              newAudiences = ["all"];
                            } else {
                              // Remove "all" if present and add new selection
                              newAudiences = [
                                ...currentAudiences.filter((a) => a !== "all"),
                                audience.value as any,
                              ];
                            }
                          } else {
                            // Remove the unchecked audience
                            newAudiences = currentAudiences.filter(
                              (a) => a !== audience.value,
                            );
                            // If no audiences selected, default to "all"
                            if (newAudiences.length === 0) {
                              newAudiences = ["all"];
                            }
                          }

                          setFormData({
                            ...formData,
                            targetAudience: newAudiences,
                          });
                        }}
                        className="w-4 h-4 text-indigo-400 border-slate-300 rounded focus:ring-indigo-400 focus:ring-2"
                      />
                      <span className="text-slate-700 font-semibold text-sm">
                        {audience.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={formData.publishDate}
                    onChange={(e) =>
                      setFormData({ ...formData, publishDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expiryDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-slate-700 font-semibold shadow-sm transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200/60">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedNotice(null);
                  resetForm();
                }}
                className="px-6 py-2.5 text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-all duration-300 hover:scale-105 font-bold shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updateNoticeMutation.isPending}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-300 to-indigo-400 text-white rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:from-slate-300 disabled:to-slate-300 disabled:cursor-not-allowed font-bold flex items-center gap-2"
              >
                {updateNoticeMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating...
                  </>
                ) : (
                  "Update Announcement"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Announcement Modal */}
      {showViewModal && selectedNotice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-indigo-300 to-indigo-400 p-6 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Announcement Details
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedNotice(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  {selectedNotice.title}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-3 py-1 text-xs rounded-xl ${getTypeColor(
                      selectedNotice.type,
                    )}`}
                  >
                    {selectedNotice.type}
                  </span>
                  <span
                    className={`px-3 py-1 text-xs rounded-xl ${getPriorityColor(
                      selectedNotice.priority,
                    )}`}
                  >
                    {selectedNotice.priority}
                  </span>
                  {!selectedNotice.isActive && (
                    <span className="px-3 py-1 text-xs font-bold rounded-xl bg-gradient-to-br from-slate-300 to-slate-400 text-white shadow-sm">
                      Inactive
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="bg-slate-50 p-6 rounded-xl">
                <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">
                  Content
                </h4>
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {selectedNotice.content}
                </p>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Target Audience
                    </span>
                  </div>
                  <p className="text-slate-900 font-semibold capitalize">
                    {Array.isArray(selectedNotice.targetAudience)
                      ? selectedNotice.targetAudience.join(", ")
                      : selectedNotice.targetAudience}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Published Date
                    </span>
                  </div>
                  <p className="text-slate-900 font-semibold">
                    {new Date(selectedNotice.publishDate).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}
                  </p>
                </div>

                {selectedNotice.expiryDate && (
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Expiry Date
                      </span>
                    </div>
                    <p className="text-slate-900 font-semibold">
                      {new Date(selectedNotice.expiryDate).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )}
                    </p>
                  </div>
                )}

                {selectedNotice.author && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Author
                      </span>
                    </div>
                    <p className="text-slate-900 font-semibold">
                      {selectedNotice.author.name}
                    </p>
                    <p className="text-slate-600 text-sm capitalize">
                      {selectedNotice.author.department}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedNotice(null);
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-300 to-indigo-400 text-white rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecretaryAnnouncements;
